import { z } from 'zod';

/**
 * Contract for the two liveness endpoints. Lives in `shared` because the web
 * shell renders the API status and must not guess the payload shape.
 */

export const healthResponseSchema = z.strictObject({
  status: z.literal('ok'),
  version: z.string(),
});

export type HealthResponse = z.infer<typeof healthResponseSchema>;

/**
 * The webhook INGEST leg, reported here on purpose (webhooks.md section 2).
 *
 * Depth and the age of the oldest undispatched row are the two numbers that distinguish
 * the three states an operator cares about - worker down, dispatcher wedged on dead rows,
 * endpoint slow. Without them a stopped worker looks exactly like a quiet afternoon.
 *
 * Absent from a 503: when the database is down there is nothing to count, and inventing a
 * zero would report an empty outbox during precisely the outage that fills it.
 */
export const webhookOutboxHealthSchema = z.strictObject({
  depth: z.number().int(),
  dead: z.number().int(),
  oldestAgeSeconds: z.number().int().nullable(),
});

/**
 * The WORKFLOW engine's two numbers (workflow-builder.md D44), beside the webhook block and
 * absent for the same reason.
 *
 * `stuck` is runs still `running` past the job's own expiry - a worker killed mid-step, or
 * a run nothing is executing. `failedLastHour` is the other half of the same question: a
 * quiet afternoon and an endpoint that has been refusing every call for an hour both show a
 * stuck count of zero.
 */
export const workflowHealthSchema = z.strictObject({
  stuck: z.number().int(),
  failedLastHour: z.number().int(),
});

/**
 * THE CSV ENGINE'S STUCK-RUN LINE (csv-import-export.md D41, D42), beside the two above and
 * absent on a 503 for the same reason. D42 puts stuck EXPORTS on this SAME line rather than
 * in a block of their own - each run kind measured against its own queue's expiry - because
 * the operator's move is identical for both: press Retry on the run it points at.
 *
 * It matters more than the workflow equivalent, and D30 is why: the partial unique index on
 * `('queued','running')` means ONE wedged run blocks every future import into that object.
 * With `retryLimit: 0` nothing redelivers the job, so the recovery is an operator pressing
 * Retry - and they can only do that if something told them. `oldestStuckSeconds` is what
 * separates "a deploy killed a worker a minute ago" from "this object has been blocked since
 * Tuesday".
 */
export const importHealthSchema = z.strictObject({
  stuck: z.number().int(),
  oldestStuckSeconds: z.number().int().nullable(),
});

/**
 * THE INBOUND INTAKE BLOCK (`docs/decisions/inbound-intake.md` D17), beside the three above
 * and absent on a 503 for the same reason.
 *
 * A delivery that was RECEIVED and never applied is the failure this block exists to show,
 * and it is invisible everywhere else: the sender got its 202, the envelope record exists
 * with its document and its file, and only the STATUS says that nothing was written from it.
 * `oldestUnappliedSeconds` is what separates "a worker restarted a minute ago" from "nothing
 * has been mapped since Tuesday".
 *
 * The per-source lines carry the PULL's own two facts, because a pull that stopped is the
 * other silent failure: `lastPullError` is non-null exactly while the pull is paused, so the
 * reason a source went quiet is on the same page as the counts.
 */
export const intakeSourceHealthSchema = z.strictObject({
  key: z.string(),
  lastPullAt: z.string().nullable(),
  lastPullError: z.string().nullable(),
});

export const intakeHealthSchema = z.strictObject({
  /**
   * A2: WHY THE DELIVERY OBJECT IS NOT READY, or null when it is.
   *
   * The intake module owns the object every delivery is stored on and ensures it at boot;
   * a boot that could not finish that LOGS and carries on, because taking the whole CRM
   * down over one module's door is the worse trade. This is the other half of that
   * decision: a module that fails quietly and is reported nowhere has just chosen to fail
   * silently, which is what D17 exists to prevent. Non-null means no source can be created
   * or saved, and the sentence says what to look at.
   */
  objectFailure: z.string().nullable(),
  activeSources: z.number().int(),
  received: z.number().int(),
  needsReview: z.number().int(),
  failed: z.number().int(),
  oldestUnappliedSeconds: z.number().int().nullable(),
  sources: z.array(intakeSourceHealthSchema),
});

export type IntakeHealth = z.infer<typeof intakeHealthSchema>;

/**
 * THE HEALTH ENGINE'S LAST RUN (`docs/decisions/phase-b-coach.md` D13), beside the four
 * blocks above and absent on a 503 for the same reason.
 *
 * The failure it exists for is the quietest one in the product: the engine writes a colour
 * onto a customer and files a task about it, and when it stops NOTHING looks wrong. Every
 * enrollment keeps the colour it had, the dashboard keeps listing it, and the only symptom
 * is that a customer who went silent last Tuesday is still green. There is no queue depth
 * to watch, because a nightly recompute leaves no backlog behind when it does not run.
 *
 * So the block carries WHEN it last finished rather than how much work is waiting.
 * `lastRunAt` null means the engine has never completed a run on this instance - which is
 * a different fact from the block being absent, and the two must not be collapsed: absent
 * says the marker could not be read, null says nothing has ever run.
 *
 * `lastError` is the other half. A run that threw still writes the marker, so a nightly
 * job that has been failing since Tuesday says so here rather than looking like a job that
 * simply has not been due yet.
 */
export const healthEngineHealthSchema = z.strictObject({
  lastRunAt: z.string().nullable(),
  lastDurationMs: z.number().int().nullable(),
  lastError: z.string().nullable(),
  /** Records the last run scored, over every configured object. Null before the first run. */
  scored: z.number().int().nullable(),
  /** How many of them the run actually wrote to. A healthy steady state is zero. */
  changed: z.number().int().nullable(),
  /**
   * THE TWO SILENT FAILURES, and they are on this block for the reason the block exists.
   *
   * `blocked` counts writes a human correction refused (INV-5): that customer's colour is
   * frozen at whatever somebody typed, and no screen says so. `failed` counts records the
   * pass stepped over because scoring them threw. Without them a run that could not score
   * forty customers reports "scored 212, changed 0" - which is exactly what a healthy night
   * looks like.
   */
  blocked: z.number().int().nullable(),
  failed: z.number().int().nullable(),
});

export type HealthEngineHealth = z.infer<typeof healthEngineHealthSchema>;

/**
 * THE WORKER'S OWN PULSE (`docs/decisions/operations.md` D20 and D21, closing
 * `admin-area.md` open risk 6).
 *
 * It is the one block none of the five above can stand in for, and D25 of `admin-area.md`
 * says why in its own words: depth and age answer "is the worker alive" only on an instance
 * WITH webhook traffic. An instance with no subscriptions has a permanently empty outbox, a
 * nightly recompute leaves no backlog behind when it does not run, and a stopped worker is
 * then invisible until a phase-B index never appears or proposals stop expiring. So the
 * worker says so itself, every thirty seconds, into a row that exists for nothing else.
 *
 * THREE STATES AND NOT TWO. `never` is "no worker has ever written the row on this
 * instance" - a fresh install, or a deploy where the worker container was never started -
 * and it must not be collapsed into `stale`, which is "a worker ran here and has stopped".
 * They are different operator moves: one is a compose file that is missing a service, the
 * other is a process that died.
 *
 * Absent from a 503 and absent on a read failure, like the five blocks above: a missing
 * block says the row could not be read, and inventing a `never` would report "you have
 * never run a worker" during exactly the database outage that hid it.
 */
export const workerHealthSchema = z.strictObject({
  status: z.literal(['alive', 'stale', 'never']),
  /** When the last beat landed, or null when no worker has ever written the row. */
  beatAt: z.string().nullable(),
  /** Seconds since that beat, null for the same reason. */
  ageSeconds: z.number().int().nullable(),
  /** Hostname plus pid of the process that wrote it, so two containers are tellable apart. */
  workerId: z.string().nullable(),
  /** The worker's build. A server and a worker on different versions is its own incident. */
  version: z.string().nullable(),
});

export type WorkerHealth = z.infer<typeof workerHealthSchema>;

/**
 * HOW OFTEN THE WORKER BEATS, and how long a beat has to be missing before the row is
 * called stale. Both live here rather than in `config.ts` because the WRITER (the worker)
 * and the READER (the server's `/ready` and its liveness watch) are different processes:
 * an environment variable that only one of them read would make the two disagree about
 * what "stale" means, which is the one thing this pair must not do.
 *
 * SIX MISSED BEATS, not one. A single missed write is a slow query or a paused container
 * during a deploy, and paging on it teaches an operator to ignore the page. Three minutes
 * is long enough that every ordinary hiccup passes and short enough that nobody's morning
 * starts with a night of unprocessed jobs.
 */
export const WORKER_HEARTBEAT_INTERVAL_SECONDS = 30;
export const WORKER_HEARTBEAT_STALE_SECONDS = 180;

/**
 * THE FULFILLMENT FEEDS' LIVENESS (package P7): one entry per feed run history, read from
 * `meta.feed_run`. `lastSuccessAt` is the marker the production-hygiene rule asks for - it
 * moves only when a run WORKED, so a feed with nothing to change and a feed that stopped are
 * two different answers. All nulls means the feed has never run here, which is also what a
 * feed that is switched off (its env variable unset) looks like.
 */
export const feedHealthSchema = z.strictObject({
  /** The Fathom account label; absent for the Telegram touch feed. */
  account: z.string().optional(),
  lastRunAt: z.string().nullable(),
  lastSuccessAt: z.string().nullable(),
  /** The error of the newest run when it failed, null when it worked. */
  lastError: z.string().nullable(),
  /** The newest run's counts (read, changed, blocked, failed, ...). */
  counts: z.record(z.string(), z.number()).nullable(),
});

export type FeedHealth = z.infer<typeof feedHealthSchema>;

export const feedsHealthSchema = z.strictObject({
  touch: feedHealthSchema,
  /** One entry per Fathom account that has ever run. */
  fathom: z.array(feedHealthSchema),
  /**
   * The weekly check-in reader (redesign backend BE-CHECKIN). Its counts: `messages` scanned,
   * `fills`, pulses `created`, `replaced` and `filled`, `unchanged`, and the skips by reason
   * (`skippedNoAgency`, `skippedAmbiguousAgency`, `skippedNoActiveEnrollment`,
   * `skippedDuplicatePulse`, `skippedDeleted`).
   */
  checkin: feedHealthSchema,
});

export type FeedsHealth = z.infer<typeof feedsHealthSchema>;

export const readyResponseSchema = z.strictObject({
  status: z.literal(['ready', 'unavailable']),
  database: z.literal(['up', 'down']),
  webhooks: webhookOutboxHealthSchema.optional(),
  workflows: workflowHealthSchema.optional(),
  imports: importHealthSchema.optional(),
  intake: intakeHealthSchema.optional(),
  healthEngine: healthEngineHealthSchema.optional(),
  worker: workerHealthSchema.optional(),
  feeds: feedsHealthSchema.optional(),
});

export type ReadyResponse = z.infer<typeof readyResponseSchema>;
